import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Search, Building2, MoreHorizontal, Eye, Edit, Trash2, FileText, CheckCircle } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { clientsAPI } from '@/lib/api';
import { toast } from 'sonner';

const ENTITY_TYPE_LABELS = {
  'private_limited': 'Pvt Ltd',
  'llp': 'LLP',
  'partnership': 'Partnership',
  'sole_proprietorship': 'Sole Prop',
  'individual': 'Individual',
  'micro_business': 'Micro Business',
  'nonprofit': 'Non-Profit',
};

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll();
      setClients(response.data);
    } catch (error) {
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this client?')) return;
    try {
      await clientsAPI.delete(id);
      setClients(clients.filter(c => c.id !== id));
      toast.success('Client deleted successfully');
    } catch (error) {
      toast.error('Failed to delete client');
    }
  };

  const getClientName = (client) => {
    return client.entity_name || client.company_name || 'Unnamed Client';
  };

  const getClientContact = (client) => {
    return {
      name: client.contact_name || client.contact_person || '-',
      email: client.contact_email || client.email || '-',
      phone: client.contact_phone || client.phone || '-',
    };
  };

  const getEntityType = (client) => {
    return client.entity_type || client.client_type || 'unknown';
  };

  const filteredClients = clients.filter(client => {
    const name = getClientName(client).toLowerCase();
    const contact = getClientContact(client);
    const matchesSearch = name.includes(searchQuery.toLowerCase()) ||
                         contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         contact.email.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (filter === 'all') return matchesSearch;
    if (filter === 'draft') return matchesSearch && client.status === 'draft';
    if (filter === 'complete') return matchesSearch && client.status === 'complete';
    return matchesSearch && getEntityType(client) === filter;
  });

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-clients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Clients</h1>
          <p className="text-slate-600">{clients.length} total clients</p>
        </div>
        <Link to="/admin/clients/new">
          <Button className="bg-[#E84C3D] hover:bg-[#C0392B]" data-testid="add-client-btn">
            <Plus size={18} className="mr-2" />
            Add New Client
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-11"
                data-testid="search-clients-input"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: 'all', label: 'All' },
                { value: 'draft', label: '📝 Drafts' },
                { value: 'complete', label: '✅ Complete' },
              ].map((f) => (
                <Button
                  key={f.value}
                  variant={filter === f.value ? 'default' : 'outline'}
                  onClick={() => setFilter(f.value)}
                  className={filter === f.value ? 'bg-[#E84C3D] hover:bg-[#C0392B]' : ''}
                  data-testid={`filter-${f.value}`}
                >
                  {f.label}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clients List */}
      {filteredClients.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No clients found</h3>
            <p className="text-slate-500 mb-6">
              {searchQuery ? 'Try adjusting your search or filters' : 'Get started by adding your first client'}
            </p>
            {!searchQuery && (
              <Link to="/admin/clients/new">
                <Button className="bg-[#E84C3D] hover:bg-[#C0392B]">
                  Add Your First Client
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Entity Name</th>
                    <th>Type</th>
                    <th>Contact</th>
                    <th>Status</th>
                    <th>Documents</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredClients.map((client) => {
                    const contact = getClientContact(client);
                    const entityType = getEntityType(client);
                    return (
                      <tr key={client.id} data-testid={`client-row-${client.id}`}>
                        <td>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                              <Building2 className="w-5 h-5 text-slate-400" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2 flex-wrap">
                                <p className="font-medium text-slate-900">{getClientName(client)}</p>
                                {client.is_micro_cfo && (
                                  <Link
                                    to={`/admin/clients/${client.id}/workspace`}
                                    className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-teal-100 text-teal-700 border border-teal-300 hover:bg-teal-200"
                                    data-testid={`micro-cfo-pill-${client.id}`}
                                    title="Open Micro CFO Workspace"
                                  >
                                    Micro CFO ↗
                                  </Link>
                                )}
                              </div>
                              <p className="text-sm text-slate-500">{client.gstin || client.pan || client.cin || '-'}</p>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="text-sm px-2 py-1 rounded bg-slate-100 text-slate-700">
                            {ENTITY_TYPE_LABELS[entityType] || entityType}
                          </span>
                        </td>
                        <td>
                          <p className="text-slate-900">{contact.name}</p>
                          <p className="text-sm text-slate-500">{contact.phone}</p>
                        </td>
                        <td>
                          {client.status === 'complete' ? (
                            <span className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded bg-green-100 text-green-700">
                              <CheckCircle size={14} />
                              Complete
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-sm px-2 py-1 rounded bg-yellow-100 text-yellow-700">
                              <FileText size={14} />
                              Draft
                            </span>
                          )}
                        </td>
                        <td>
                          <span className="text-slate-600">{client.document_count || 0} files</span>
                        </td>
                        <td>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" data-testid={`client-actions-${client.id}`}>
                                <MoreHorizontal size={18} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {client.is_micro_cfo && (
                                <DropdownMenuItem asChild>
                                  <Link to={`/admin/clients/${client.id}/workspace`} className="flex items-center gap-2 text-teal-700">
                                    <Eye size={16} /> Open Micro CFO Workspace
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem asChild>
                                <Link to={`/admin/clients/${client.id}`} className="flex items-center gap-2">
                                  <Eye size={16} /> View Details
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem asChild>
                                <Link to={`/admin/clients/${client.id}/edit`} className="flex items-center gap-2">
                                  <Edit size={16} /> Edit
                                </Link>
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => handleDelete(client.id)}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 size={16} className="mr-2" /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
